import { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Catches render-time errors from its children and shows a visible error
 * card instead of a blank page. Without this, a thrown error inside the
 * subtree (e.g. a runtime exception in a form component) would unmount
 * everything below the parent Outlet, leaving an empty page.
 */
export class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to the console so DevTools shows the real stack trace.
    console.error("[RouteErrorBoundary]", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || "Unknown error";
      return (
        <div className="p-6 max-w-2xl mx-auto" data-testid="route-error-boundary">
          <div className="border-2 border-rose-500/60 bg-rose-500/10 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-rose-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h2 className="font-mono font-bold text-card-foreground">Page error</h2>
                <p className="font-mono text-xs text-muted-foreground mt-1 break-words">
                  This page hit an unexpected error and stopped rendering.
                </p>
                <pre className="mt-3 text-[11px] font-mono bg-background/60 border border-rose-500/30 rounded p-2 overflow-x-auto" data-testid="route-error-message">{msg}</pre>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={this.reset}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-mono hover:bg-primary/90"
                    data-testid="route-error-retry"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => { window.location.href = "/"; }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs font-mono hover:bg-secondary/50"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default RouteErrorBoundary;
